import React, { useState } from 'react';
import '../styles/SubdivisionAnalysis.css';
import Badge from './Badge';
import { IconButton } from './';
import { Chevron, Location, House } from '../assets/icons';
import { colour } from '../styles/GlobalStyles';
import { renderHTML } from '../functions';
import { HugeiconsIcon } from '@hugeicons/react';
import { Location04Icon } from '@hugeicons/core-free-icons';

function SubdivisionAnalysis(props) {
    const { fullSubdivisionAnalysis, darkMode = false, allComps = [], viewComp = null } = props;

    // State for expanded subdivisions (must be called before any early returns)
    const [expandedSubdivisions, setExpandedSubdivisions] = useState({});

    // Early return if no data
    if (!fullSubdivisionAnalysis || !fullSubdivisionAnalysis.results || fullSubdivisionAnalysis.results.length === 0) {
        return null;
    }

    const textColorClass = darkMode ? 'colour-white' : 'colour-primary';
    const secondaryTextColorClass = darkMode ? 'colour-tertiary' : 'colour-secondary';
    const containerClass = darkMode ? 'bg-colour-dark-mode' : 'bg-colour-white';

    const { subjectSubdivision, results } = fullSubdivisionAnalysis;

    // Sort results by distance from closest to furthest
    const sortedResults = [...results].sort((a, b) => {
        const distanceA = a.distanceFromSubject;
        const distanceB = b.distanceFromSubject;
        
        // Handle null/undefined values - put them at the end
        if (distanceA === null || distanceA === undefined) return 1;
        if (distanceB === null || distanceB === undefined) return -1;
        
        // If distance is a string (e.g., "0.5 miles"), extract the number
        const numA = typeof distanceA === 'string' ? parseFloat(distanceA.replace(/[^\d.]/g, '')) : distanceA;
        const numB = typeof distanceB === 'string' ? parseFloat(distanceB.replace(/[^\d.]/g, '')) : distanceB;
        
        return numA - numB;
    });

    // Group results by verdict (using original results for badges)
    const higherPriced = results.filter(r => r.verdict === 'premium');
    const lowerPriced = results.filter(r => r.verdict === 'discount');
    const kept = results.filter(r => r.verdict === 'kept');
    const excluded = results.filter(r => r.verdict === 'excluded');

    // Generate analysis text (exclude "kept" subdivisions from avoid count)
    const totalAvoid = results.filter(r => r.verdict !== 'kept').length;
    const analysisText = `We found ${totalAvoid} subdivision${totalAvoid !== 1 ? 's' : ''} to avoid when looking for comps due to mis-matched price drivers.`;

    // Get map image (use subject subdivision's roadmap image)
    const mapImage = subjectSubdivision?.images?.roadmap || null;

    const toggleSubdivision = (subdivisionName) => {
        setExpandedSubdivisions(prev => ({
            ...prev,
            [subdivisionName]: !prev[subdivisionName]
        }));
    };

    return (
        <div className={`subdivision-analysis-container ${containerClass}`}>
            {/* Header Section */}
            <div className="subdivision-analysis-header-section">
                <h3 className={`heading-small-bold ${textColorClass}`}>
                    Subdivision analysis
                </h3>
                {analysisText && (
                    <p className={`body-regular ${textColorClass}`}>
                        {analysisText}
                    </p>
                )}
            </div>

            {/* Map Section */}
            {mapImage && (
                <div className="subdivision-analysis-map-section">
                    <div className="subdivision-analysis-map-container">
                        <img
                            src={mapImage}
                            alt="Subdivision map"
                            className="subdivision-analysis-map-image"
                        />
                        {/* Legend badges */}
                        <div className="subdivision-analysis-map-legend">
                            {kept.length > 0 ? (
                                <Badge
                                    bgColour="bg-colour-green-01"
                                    statusDot={false}
                                    statusDotColour={null}
                                    text="Kept"
                                    textClass="body-regular colour-primary"
                                    icon={null}
                                    tooltip={false}
                                    tooltipBody={null}
                                    tooltipPosition={null}
                                    iconLeft={null}
                                />
                            ) : excluded.length > 0 ? (
                                <Badge
                                    bgColour="bg-colour-yellow-01"
                                    statusDot={false}
                                    statusDotColour={null}
                                    text="Excluded"
                                    textClass="body-regular colour-primary"
                                    icon={null}
                                    tooltip={false}
                                    tooltipBody={null}
                                    tooltipPosition={null}
                                    iconLeft={null}
                                />
                            ) : higherPriced.length > 0 ? (
                                <Badge
                                    bgColour="bg-colour-purple-01"
                                    statusDot={false}
                                    statusDotColour={null}
                                    text="Higher-priced"
                                    textClass="body-regular colour-primary"
                                    icon={null}
                                    tooltip={false}
                                    tooltipBody={null}
                                    tooltipPosition={null}
                                    iconLeft={null}
                                />
                            ) : (
                                <Badge
                                    bgColour="bg-colour-red-01"
                                    statusDot={false}
                                    statusDotColour={null}
                                    text="Lower-priced"
                                    textClass="body-regular colour-primary"
                                    icon={null}
                                    tooltip={false}
                                    tooltipBody={null}
                                    tooltipPosition={null}
                                    iconLeft={null}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Avoid Comps Section */}
            {(higherPriced.length > 0 || lowerPriced.length > 0) && (
                <div className="subdivision-analysis-avoid-section">
                    <h4 className={`body-bold ${textColorClass}`}>
                        Avoid comps in these subdivisions
                    </h4>
                    {higherPriced.length > 0 && (
                        <>
                            <p className={`body-regular ${textColorClass}`}>
                                Higher-priced
                            </p>
                            <div className="subdivision-analysis-badges-row">
                                {higherPriced.map((subdivision, index) => (
                                    <Badge
                                        key={index}
                                        bgColour="bg-colour-purple-01"
                                        statusDot={false}
                                        statusDotColour={null}
                                        text={subdivision.subdivision}
                                        textClass="body-regular colour-primary"
                                        icon={null}
                                        tooltip={false}
                                        tooltipBody={null}
                                        tooltipPosition={null}
                                        iconLeft={null}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                    {lowerPriced.length > 0 && (
                        <>
                            <p className={`body-regular ${textColorClass}`}>
                                Lower-priced
                            </p>
                            <div className="subdivision-analysis-badges-row">
                                {lowerPriced.map((subdivision, index) => (
                                    <Badge
                                        key={index}
                                        bgColour="bg-colour-red-01"
                                        statusDot={false}
                                        statusDotColour={null}
                                        text={subdivision.subdivision}
                                        textClass="body-regular colour-primary"
                                        icon={null}
                                        tooltip={false}
                                        tooltipBody={null}
                                        tooltipPosition={null}
                                        iconLeft={null}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
            {/* Kept Comps Section */}
            {kept.length > 0 && (
                <div className="subdivision-analysis-avoid-section">
                    <h4 className={`body-bold ${textColorClass}`}>
                        Keep comps in these subdivisions
                    </h4>
                    <div className="subdivision-analysis-badges-row">
                        {kept.map((subdivision, index) => (
                            <Badge
                                key={index}
                                bgColour="bg-colour-green-01"
                                statusDot={false}
                                statusDotColour={null}
                                text={subdivision.subdivision}
                                textClass="body-regular colour-primary"
                                icon={null}
                                tooltip={false}
                                tooltipBody={null}
                                tooltipPosition={null}
                                iconLeft={null}
                            />
                        ))}
                    </div>
                </div>
            )}
            {/* Excluded Comps Section */}
            {excluded.length > 0 && (
                <div className="subdivision-analysis-avoid-section">
                    <h4 className={`body-bold ${textColorClass}`}>
                        Excluded subdivisions
                    </h4>
                    <p className={`body-regular ${textColorClass}`}>
                        Excluded
                    </p>
                    <div className="subdivision-analysis-badges-row">
                        {excluded.map((subdivision, index) => (
                            <Badge
                                key={index}
                                bgColour="bg-colour-yellow-01"
                                statusDot={false}
                                statusDotColour={null}
                                text={subdivision.subdivision}
                                textClass="body-regular colour-primary"
                                icon={null}
                                tooltip={false}
                                tooltipBody={null}
                                tooltipPosition={null}
                                iconLeft={null}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Subdivisions Detail Loop */}
            {sortedResults.length > 0 && (
                <div className="subdivision-analysis-details-section">
                    {sortedResults.map((subdivision, index) => {
                        const isExpanded = expandedSubdivisions[subdivision.subdivision] || false;
                        const distance = subdivision.distanceFromSubject;
                        
                        return (
                            <div key={index} className="subdivision-analysis-detail-item">
                                <div className="subdivision-analysis-detail-header">
                                    <IconButton
                                        size="x-small"
                                        icon={<Chevron 
                                            fill={colour.gray06}
                                            className={isExpanded ? "subdivision-chevron-expanded" : "subdivision-chevron-collapsed"}
                                        />}
                                        disabled={false}
                                        clickFunc={() => toggleSubdivision(subdivision.subdivision)}
                                        standardIcon={false}
                                        background={false}
                                        bgType={null}
                                        contextTooltip={false}
                                        contextTooltipBG={null}
                                        contextTooltipText={null}
                                        customClass={null}
                                    />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <h4 
                                            className={`body-bold ${textColorClass} cursor-pointer`}
                                            onClick={() => toggleSubdivision(subdivision.subdivision)}
                                        >
                                            {subdivision.subdivision}
                                        </h4>
                                        {subdivision.zpids && subdivision.zpids.length > 0 && viewComp && allComps.length > 0 && (
                                            <IconButton
                                                size="x-small"
                                                icon={<HugeiconsIcon icon={Location04Icon} size={16} color={colour.gray06} />}
                                                disabled={false}
                                                clickFunc={() => {
                                                    const firstZpid = subdivision.zpids[0];
                                                    const comp = allComps.find(c => String(c?.uniqueIds?.zillowId) === String(firstZpid));
                                                    if (comp) {
                                                        viewComp(comp);
                                                    }
                                                }}
                                                standardIcon={false}
                                                background={false}
                                                bgType={null}
                                                contextTooltip={false}
                                                contextTooltipBG={null}
                                                contextTooltipText={null}
                                                customClass={null}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className={`subdivision-analysis-detail-content ${isExpanded ? 'subdivision-analysis-detail-content-expanded' : 'subdivision-analysis-detail-content-collapsed'}`}>
                                    <div className="subdivision-analysis-detail-badges">
                                        {distance && (
                                            <Badge
                                                bgColour="bg-colour-gray-02"
                                                statusDot={false}
                                                statusDotColour={null}
                                                text={distance}
                                                textClass="body-semibold colour-primary"
                                                icon={<Location fill={colour.gray06} size={16} />}
                                                tooltip={false}
                                                tooltipBody={null}
                                                tooltipPosition={null}
                                                iconLeft={true}
                                            />
                                        )}
                                            {subdivision.totalComps > 0 && (
                                                <Badge
                                                    bgColour={
                                                        subdivision.verdict === "kept" ? "bg-colour-green-01" :
                                                        subdivision.verdict === "excluded" ? "bg-colour-yellow-01" :
                                                        subdivision.verdict === "discount" ? "bg-colour-red-01" : 
                                                        "bg-colour-purple-01"
                                                    }
                                                    statusDot={false}
                                                    statusDotColour={null}
                                                    text={`${subdivision.totalComps} comp${subdivision.totalComps !== 1 ? 's' : ''}`}
                                                    textClass="body-semibold colour-primary"
                                                    icon={<House fill={
                                                        subdivision.verdict === "kept" ? colour.green02 :
                                                        subdivision.verdict === "excluded" ? colour.yellow02 :
                                                        subdivision.verdict === "discount" ? colour.red02 : 
                                                        colour.purple02
                                                    } size={16} />}
                                                    tooltip={false}
                                                    tooltipBody={null}
                                                    tooltipPosition={null}
                                                    iconLeft={true}
                                                />
                                            )}
                                    </div>
                                    {subdivision.analysis && (
                                        <div 
                                            className={`body-regular ${secondaryTextColorClass}`}
                                            dangerouslySetInnerHTML={{ __html: renderHTML(subdivision.analysis, [], {}) }}
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default SubdivisionAnalysis;

